/*
 * Viretta – header menus (Impact 7.2 port, step 11b). Loaded with defer by sections/header.liquid.
 *
 * 1. Desktop mega menu (.viretta-mega): hovering a sub link shows its panel; leaving the menu resets to the first one.
 * 2. Drag-to-scroll for the product / blog rails (desktop [data-viretta-drag-scroll], mobile [data-mobile-slider]);
 *    a drag does not trigger the link or button under the pointer.
 * 3. Mobile menu: accordions with the same name attribute close each other (fallback for browsers without native
 *    exclusive <details name>).
 * 4. Newsletter forms in the menus (.header-slider-newsletter__form): Klaviyo client subscription – one handler for
 *    all forms (the old inline script was rendered once per form and bound every form again).
 *
 * Everything is delegated on document, so it also works after the theme editor re-renders the header section.
 */
(() => {
  if (window.virettaHeaderInitialized) return;
  window.virettaHeaderInitialized = true;

  /* 1. Mega menu panels --------------------------------------------------------------------------------------- */

  const setActivePanel = (container, item) => {
    const items = Array.from(container.querySelectorAll('.hover-nav-item'));
    const panels = Array.from(container.querySelectorAll('.hover-panel-content'));
    const index = Math.max(0, items.indexOf(item));

    items.forEach((element, i) => element.classList.toggle('active', i === index));
    panels.forEach((element, i) => element.classList.toggle('active', i === index));
  };

  document.addEventListener('mouseover', (event) => {
    const item = event.target.closest?.('[data-viretta-mega] .hover-nav-item');
    if (!item || item.classList.contains('active')) return;
    setActivePanel(item.closest('[data-viretta-mega]'), item);
  });

  document.addEventListener('mouseout', (event) => {
    const container = event.target.closest?.('[data-viretta-mega]');
    if (!container || container.contains(event.relatedTarget)) return;
    setActivePanel(container, container.querySelector('.hover-nav-item'));
  });

  /* 2. Drag to scroll ----------------------------------------------------------------------------------------- */

  const DRAG_SELECTOR = '[data-viretta-drag-scroll], [data-mobile-slider]';
  let drag = null;

  document.addEventListener('pointerdown', (event) => {
    const scroller = event.target.closest?.(DRAG_SELECTOR);
    if (!scroller || (event.pointerType === 'mouse' && event.button !== 0)) return;

    // The pointer is followed on document, so no pointer capture is needed (capture would retarget the click to the
    // scroller and break the links / add buttons inside the rail).
    drag = { scroller, pointerId: event.pointerId, startX: event.clientX, scrollLeft: scroller.scrollLeft, frame: null };
    scroller.dataset.dragged = 'false';
    scroller.classList.add('is-dragging');
  });

  document.addEventListener('pointermove', (event) => {
    if (!drag || drag.pointerId !== event.pointerId) return;

    const walk = (event.clientX - drag.startX) * 1.25;
    if (Math.abs(walk) > 4) drag.scroller.dataset.dragged = 'true';

    cancelAnimationFrame(drag.frame);
    const { scroller, scrollLeft } = drag;
    drag.frame = requestAnimationFrame(() => { scroller.scrollLeft = scrollLeft - walk; });
  });

  const endDrag = () => {
    if (!drag) return;
    drag.scroller.classList.remove('is-dragging');
    drag = null;
  };

  ['pointerup', 'pointercancel'].forEach((type) => document.addEventListener(type, endDrag));

  // A drag does not open the link / press the button under the pointer
  document.addEventListener('click', (event) => {
    const scroller = event.target.closest?.(DRAG_SELECTOR);
    if (scroller?.dataset.dragged === 'true' && event.target.closest('a, button')) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, true);

  document.addEventListener('dragstart', (event) => {
    if (event.target.closest?.(DRAG_SELECTOR)) event.preventDefault();
  });

  /* 3. Exclusive accordions in the mobile menu ----------------------------------------------------------------- */

  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open || !details.classList.contains('mobile-accordion')) return;

    const groupName = details.getAttribute('name');
    if (!groupName) return;

    document.querySelectorAll(`details.mobile-accordion[name="${CSS.escape(groupName)}"][open]`).forEach((other) => {
      if (other !== details) other.removeAttribute('open');
    });
  }, true);

  /* 4. Newsletter forms ------------------------------------------------------------------------------------------ */

  const setMessage = (form, text, state) => {
    const message = form.querySelector('.header-slider-newsletter__message');
    if (!message) return;

    message.textContent = text;
    message.classList.toggle('header-slider-newsletter__message--success', state === 'success');
    message.classList.toggle('header-slider-newsletter__message--error', state === 'error');
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest?.('.header-slider-newsletter__form');
    if (!form) return;

    event.preventDefault();
    if (form.dataset.submitting === 'true') return;

    const email = form.querySelector('.header-slider-newsletter__input')?.value.trim();

    if (!email) {
      setMessage(form, 'Bitte gib eine gültige E-Mail-Adresse ein.', 'error');
      return;
    }

    form.dataset.submitting = 'true';
    setMessage(form, 'Wird gesendet …');

    const payload = {
      data: {
        type: 'subscription',
        attributes: { profile: { data: { type: 'profile', attributes: { email } } } },
        relationships: { list: { data: { type: 'list', id: form.dataset.klaviyoList } } }
      }
    };

    try {
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(form.dataset.klaviyoCompany)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2025-01-15' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setMessage(form, 'Danke – bitte bestätige deine Anmeldung in der E-Mail, die wir dir gerade geschickt haben.', 'success');
        form.reset();
      } else {
        setMessage(form, 'Die Anmeldung hat nicht geklappt – bitte versuch es noch einmal.', 'error');
      }
    } catch (error) {
      setMessage(form, 'Das hat nicht geklappt.', 'error');
    } finally {
      delete form.dataset.submitting;
    }
  });
})();
